import React from "react";

import * as galleryActions from "modules/gallery/actionCreator";

import { GlobalApplicationState } from "globalApplicationState";
import { connect, ConnectedProps } from "react-redux";
import { Grid, Link, Typography } from "@mui/material";

import Disclaimer from "modules/common/components/disclaimer";
import Loading from "modules/common/components/loading";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NewsletterHeaderFooterEditor from "modules/newsletters/edit/components/newsletterHeaderFooterEditor";
import InfoHover from "modules/common/components/hovers/infoHover";
import UploaderWithLoad from "modules/newsletters/common/uploader/uploaderWithLoad";

const HeaderAndFooterComponent: React.FunctionComponent<PropsWithRedux> = ({
    isCustomTemplate,
    header,
    body,
    footer,
    defaultLcid,
    isLoadingFooterImage,
    isLoadingHeaderImage,
    headerImageHeight,
    headerImageWidth,
    footerImageHeight,
    footerImageWidth,
    footerImage,
    headerImage,
    onBodyChange,
    onHeaderChange,
    onFooterChange,
    onFullscreen,
    getImage,
    setImage,
    onError,
    clearImage,
}) => {
    return (
        <>
            {isCustomTemplate ? (
                <>
                    <Disclaimer
                        icon={<InfoOutlinedIcon />}
                        text={
                            <React.Fragment>
                                <span>
                                    Header and Footer images cannot be modified through the Newsletter Editor when using custom templates.
                                    Add or modify custom templates with the help of our Support team.{" "}
                                    <Link
                                        onClick={() =>
                                            window.open(
                                                "https://support.sparrowconnected.com/en/how-do-i-create-a-custom-template-for-newsletters"
                                            )
                                        }
                                        underline="always"
                                    >
                                        Learn more
                                    </Link>
                                    .
                                </span>
                            </React.Fragment>
                        }
                    />

                    <Grid container spacing={2} style={{ padding: "20px 0px" }}>
                        <Grid item xs={3}>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Typography style={{ lineHeight: "42px" }}>Body</Typography>
                                <InfoHover>Enter any supporting messages or paragraphs.</InfoHover>
                            </div>
                        </Grid>
                        <Grid item xs={9}>
                            <NewsletterHeaderFooterEditor
                                initOptions={{
                                    max_height: 300,
                                    min_height: 200,
                                    menubar: false,
                                }}
                                getImage={getImage}
                                enableEmbeddedMedia={false}
                                enableInlineImages={true}
                                value={body || ""}
                                changedSinceSaved={false}
                                onChange={onBodyChange}
                                defaultLcid={defaultLcid}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} style={{ padding: "6px 0px" }}>
                        <Grid item xs={3}>
                            <Typography style={{ lineHeight: "42px" }}>Footer Text</Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <NewsletterHeaderFooterEditor
                                initOptions={{
                                    max_height: 300,
                                    menubar: false,
                                    min_height: 200,
                                }}
                                getImage={getImage}
                                enableEmbeddedMedia={false}
                                enableInlineImages={true}
                                value={footer || ""}
                                changedSinceSaved={false}
                                onChange={onFooterChange}
                                defaultLcid={defaultLcid}
                            />
                        </Grid>
                    </Grid>
                </>
            ) : (
                <>
                    <div>
                        <Grid container spacing={2} style={{ padding: "6px 0px" }}>
                            <Grid item xs={3}>
                                <Typography style={{ lineHeight: "42px" }}>Header Image</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                {isLoadingHeaderImage ? (
                                    <div className="image-loading">
                                        <Loading padding={12} />
                                    </div>
                                ) : (
                                    <UploaderWithLoad
                                        imageSrc={headerImage}
                                        imageType="Header"
                                        setImage={setImage}
                                        imageHeight={headerImageHeight}
                                        imageWidth={headerImageWidth}
                                        onError={onError}
                                        isLoading={isLoadingHeaderImage ?? false}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} style={{ padding: "6px 0px" }}>
                            <Grid item xs={3}>
                                <Typography style={{ lineHeight: "42px" }}>Header Text</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                <NewsletterHeaderFooterEditor
                                    initOptions={{
                                        max_height: 300,
                                        min_height: 200,
                                        menubar: false,
                                    }}
                                    getImage={getImage}
                                    enableEmbeddedMedia={false}
                                    enableInlineImages={true}
                                    value={header || ""}
                                    changedSinceSaved={false}
                                    onChange={onHeaderChange}
                                    defaultLcid={defaultLcid}
                                    onFullscreen={onFullscreen}
                                />
                            </Grid>
                        </Grid>
                    </div>
                    <div>
                        <Grid container spacing={2} style={{ padding: "6px 0px" }}>
                            <Grid item xs={3}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <Typography style={{ lineHeight: "42px" }}>Body</Typography>
                                    <InfoHover>Enter any supporting messages or paragraphs.</InfoHover>
                                </div>
                            </Grid>
                            <Grid item xs={9}>
                                <NewsletterHeaderFooterEditor
                                    initOptions={{
                                        max_height: 300,
                                        min_height: 200,
                                        menubar: false,
                                    }}
                                    getImage={getImage}
                                    enableEmbeddedMedia={false}
                                    enableInlineImages={true}
                                    value={body || ""}
                                    changedSinceSaved={false}
                                    onChange={onBodyChange}
                                    defaultLcid={defaultLcid}
                                />
                            </Grid>
                        </Grid>
                    </div>
                    <div style={{ marginTop: 10 }}>
                        <Grid container spacing={2} style={{ padding: "6px 0px" }}>
                            <Grid item xs={3}>
                                <Typography style={{ lineHeight: "42px" }}>Footer Image</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                {isLoadingFooterImage ? (
                                    <div className="image-loading">
                                        <Loading padding={12} />
                                    </div>
                                ) : (
                                    <UploaderWithLoad
                                        imageSrc={footerImage}
                                        imageType="Footer"
                                        setImage={setImage}
                                        imageHeight={footerImageHeight}
                                        imageWidth={footerImageWidth}
                                        onError={onError}
                                        isLoading={isLoadingFooterImage ?? false}
                                        onClearImage={clearImage}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} style={{ padding: "6px 0px" }}>
                            <Grid item xs={3}>
                                <Typography style={{ lineHeight: "42px" }}>Footer Text</Typography>
                            </Grid>
                            <Grid item xs={9}>
                                <NewsletterHeaderFooterEditor
                                    initOptions={{
                                        max_height: 300,
                                        menubar: false,
                                        min_height: 200,
                                    }}
                                    getImage={getImage}
                                    enableEmbeddedMedia={false}
                                    enableInlineImages={true}
                                    value={footer || ""}
                                    changedSinceSaved={false}
                                    onChange={onFooterChange}
                                    defaultLcid={defaultLcid}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </>
            )}
        </>
    );
};

interface HeaderAndFooter {
    isCustomTemplate: boolean;
    footerImage?: File | null;
    headerImage?: File | null;
    headerImageHeight: number;
    headerImageWidth: number;
    footerImageHeight: number;
    footerImageWidth: number;
    header: string;
    footer: string;
    body: string;
    defaultLcid: string;
    isLoadingHeaderImage: boolean | undefined;
    isLoadingFooterImage: boolean | undefined;
    onBodyChange: (body: string) => void;
    onHeaderChange: (header: string) => void;
    onFooterChange: (footer: string) => void;
    onFullscreen: (isFullScreen: boolean) => void;
    setImage: (imageType: string, image?: File) => void;
    onError: (message: string) => any;
    clearImage?: (imageType: string) => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: HeaderAndFooter) => ({
        ...ownProps,
        defaultLcid: state.settings.tenantSettings.defaultLCID,
    }),
    {
        getImage: galleryActions.getImage,
    }
);

type PropsWithRedux = ConnectedProps<typeof connector>;
export const HeaderAndFooter = connector(HeaderAndFooterComponent);
